import { useEffect, useRef, useState } from "react";
import { siteData } from "@/store/siteData";
import PartnerDetailModal, { PartnerType } from "./PartnerDetailModal";

const sectorIcons: Record<string, string> = {
  "Bulut & Üretkenlik Çözümleri": "ri-cloud-line",
  "Ağ & Altyapı Sistemleri": "ri-router-line",
  "Siber Güvenlik Çözümleri": "ri-shield-keyhole-line",
  "Sunucu & Depolama Sistemleri": "ri-server-line",
  "İstemci & Veri Merkezi Çözümleri": "ri-computer-line",
  "Sanallaştırma & Bulut Altyapısı": "ri-stack-line",
};

export default function PartnersGrid() {
  const [selected, setSelected] = useState<PartnerType | null>(null);
  const [visibleItems, setVisibleItems] = useState<number[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const partners = siteData.getPartners();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          partners.forEach((_, i) => {
            setTimeout(() => setVisibleItems((prev) => [...prev, i]), i * 100);
          });
        }
      },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} data-edit="partners" className="bg-[#F8FAFC] py-20 border-t border-[#E2E8F0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section intro */}
        <div className="mb-12">
          <p className="text-[#94A3B8] text-sm leading-relaxed max-w-2xl">
            Her bir ortağımızla ilgili detaylı bilgiye ulaşmak için kartları tıklayın.
            Sertifikalar, uzmanlık alanları ve ortak proje başarılarını inceleyin.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {partners.map((partner, i) => {
            const icon = sectorIcons[partner.sector] || "ri-building-4-line";
            const years = new Date().getFullYear() - parseInt(partner.since);
            return (
              <article
                key={partner.id}
                className={`group bg-[#F1F5F9] border border-[#E2E8F0] hover:border-[#3B82F6] flex flex-col transition-all duration-500 cursor-pointer ${
                  visibleItems.includes(i) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => setSelected(partner as PartnerType)}
              >
                {/* Top band */}
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={partner.heroImage}
                    alt={partner.name}
                    className="w-full h-full object-cover object-center opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#F1F5F9] to-transparent"></div>
                  {/* Sector Badge */}
                  <div className="absolute top-3 left-4 flex items-center gap-1.5 bg-[#FFFFFF]/70 px-3 py-1.5 backdrop-blur-sm border border-[#E2E8F0]/60">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${icon} text-[#334155] text-xs`}></i>
                    </div>
                    <span className="text-[#334155] text-[10px] font-semibold tracking-widest uppercase">{partner.sector}</span>
                  </div>
                </div>

                {/* Body */}
                <div className="flex-1 flex flex-col p-6 gap-5">
                  {/* Name + Short Desc */}
                  <div>
                    <h3 className="text-[#0F172A] font-bold text-lg mb-2 group-hover:text-[#334155] transition-colors leading-tight">
                      {partner.name}
                    </h3>
                    <p className="text-[#64748B] text-sm leading-relaxed line-clamp-2">{partner.shortDesc}</p>
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                    {partner.stats.map((stat, si) => (
                      <div key={si} className="bg-[#F1F5F9] group-hover:bg-[#F1F5F9] transition-colors px-3 py-3 text-center">
                        <p className="text-[#0F172A] font-black text-base leading-none mb-0.5">{stat.value}</p>
                        <p className="text-[#64748B] text-[10px] leading-tight">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {partner.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[10px] text-[#64748B] border border-[#E2E8F0] px-2 py-1 tracking-wide">
                        {tag}
                      </span>
                    ))}
                    {partner.tags.length > 3 && (
                      <span className="text-[10px] text-[#64748B] border border-[#E2E8F0] px-2 py-1">
                        +{partner.tags.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-5 border-t border-[#E2E8F0] pt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-award-line text-[#64748B] text-xs"></i>
                    </div>
                    <span className="text-[#64748B] text-xs">{partner.since}'den beri · {years} yıl</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#64748B] group-hover:text-[#334155] transition-colors">
                    <span className="text-xs font-semibold tracking-wide">Detay</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom note */}
        <p className="text-center text-[#243050] text-xs mt-10 tracking-[0.3em] uppercase">
          Tüm ortaklık sözleşmeleri aktif olup projeler devam etmektedir
        </p>
      </div>

      {/* Modal */}
      {selected && (
        <PartnerDetailModal partner={selected} onClose={() => setSelected(null)} />
      )}
    </section>
  );
}
